import React, { useState } from 'react';
import Icons from './Icons';
import { Card } from './ui';

const FantasyWeeklyCard = ({
    score,
    rank,
    bestPlayerName,
    worstPlayerName,
    bestPlayerQuotaDiff,
    FANTASY_SCORING,
    breakdown
}) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <Card
            className={`mb-4 border-l-4 ${rank === 1 ? 'border-gold-400' :
                    rank === 2 ? 'border-gray-400' :
                        rank === 3 ? 'border-orange-400' :
                            'border-transparent'
                }`}
            gradient={rank <= 3}
        >
            <div className="flex items-center justify-between" onClick={() => setExpanded(!expanded)}>
                <div className="flex items-center space-x-3 flex-1">
                    <div className={`
            flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm flex-shrink-0
            ${rank === 1 ? 'bg-gold-400 text-forest-950' :
                            rank === 2 ? 'bg-gray-300 text-gray-800' :
                                rank === 3 ? 'bg-orange-300 text-orange-900' :
                                    'bg-gray-100 text-gray-600'}
          `}>
                        {rank}
                    </div>
                    <div className="min-w-0">
                        <h3 className="font-bold text-forest-900 dark:text-gray-100 truncate pr-2">
                            {score.participant_name || score.participant_id}
                        </h3>
                        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                            {expanded ? 'Tap to collapse' : 'Tap for details'}
                            {expanded ? <Icons.ChevronUp className="w-3 h-3 ml-1" /> : <Icons.ChevronDown className="w-3 h-3 ml-1" />}
                        </div>
                    </div>
                </div>
                <div className="text-right flex-shrink-0">
                    <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{score.correctTotalPoints}</div>
                    <div className="text-xs text-gray-400 uppercase">Points</div>
                </div>
            </div>

            {expanded && (
                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                    {/* Picks Section */}
                    <div className="mb-4">
                        <h4 className="text-xs font-bold text-gray-400 uppercase mb-2">Picks</h4>
                        <div className="space-y-2">
                            {score.picks && Object.entries(score.picks).map(([tier, pick], i) => {
                                const quotaDiff = pick.score && pick.quota ? pick.score - pick.quota : null;
                                const quotaSign = quotaDiff > 0 ? '+' : '';

                                const correctQuotaPoints = quotaDiff || 0;
                                const correctSkinsPoints = (pick.skins || 0) * FANTASY_SCORING.skins;
                                const correctCtpPoints = (pick.ctps || 0) * FANTASY_SCORING.ctps;
                                const correctTotalPoints = correctQuotaPoints + correctSkinsPoints + correctCtpPoints;

                                const isBestPick = pick.name === bestPlayerName && bestPlayerQuotaDiff > 0;
                                const isWorstPick = pick.name === worstPlayerName;

                                let highlightClass = 'bg-gray-50 dark:bg-navy-700 border-gray-100 dark:border-gray-600';
                                if (isBestPick) highlightClass = 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800';
                                if (isWorstPick) highlightClass = 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800';

                                return (
                                    <div key={i} className={`p-2 rounded-lg border ${highlightClass}`}>
                                        <div className="flex justify-between items-center mb-1">
                                            <div className="flex items-center space-x-2">
                                                <span className={`
                          text-[10px] font-bold px-1.5 py-0.5 rounded
                          ${i === 0 ? 'bg-yellow-100 text-yellow-700' :
                                                        i === 1 ? 'bg-gray-200 text-gray-700' :
                                                            'bg-orange-100 text-orange-700'}
                        `}>
                                                    T{i + 1}
                                                </span>
                                                <span className={`font-medium text-sm ${isBestPick ? 'text-green-800 dark:text-green-300' :
                                                        isWorstPick ? 'text-red-800 dark:text-red-300' : 'text-gray-700 dark:text-gray-200'
                                                    }`}>
                                                    {pick.name}
                                                </span>
                                            </div>
                                            <span className={`font-bold text-sm ${correctTotalPoints >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {correctTotalPoints}pts
                                            </span>
                                        </div>

                                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 px-1">
                                            <span>Quota: <span className={quotaDiff >= 0 ? 'text-green-600' : 'text-red-600'}>{quotaDiff !== null ? `${quotaSign}${quotaDiff}` : '-'}</span></span>
                                            {pick.skins > 0 && <span className="text-blue-600 flex items-center"><Icons.Target className="w-3 h-3 mr-0.5" /> {pick.skins}</span>}
                                            {pick.ctps > 0 && <span className="text-purple-600 flex items-center"><Icons.Location className="w-3 h-3 mr-0.5" /> {pick.ctps}</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Breakdown Section */}
                    <div>
                        <h4 className="text-xs font-bold text-gray-400 uppercase mb-2">Score Breakdown</h4>
                        <div className="bg-gray-50 dark:bg-navy-700 p-3 rounded-lg dark:text-gray-200">
                            {breakdown}
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
};

export default FantasyWeeklyCard;
